import { FinancialNominalItemDTO, FinancialServiceDTO } from '../types/FinancialDTO';

export type ServiceChange = 'UP' | 'DOWN' | 'SAME' | 'NEW' | 'GONE';
export type ItemSide = 'ADDED' | 'REMOVED' | 'SAME';

export interface ServiceDiff {
	service: string;
	previous?: FinancialServiceDTO;
	current?: FinancialServiceDTO;
	change: ServiceChange;
}

export interface ItemDiff {
	item: FinancialNominalItemDTO;
	side: ItemSide;
}

const changeOf = (previous?: FinancialServiceDTO, current?: FinancialServiceDTO): ServiceChange => {
	if (!previous) {
		return 'NEW';
	}
	if (!current) {
		return 'GONE';
	}
	const before = previous.value || 0;
	const after = current.value || 0;
	if (after > before + 0.004 || (current.quantity || 0) > (previous.quantity || 0)) {
		return 'UP';
	}
	if (after < before - 0.004 || (current.quantity || 0) < (previous.quantity || 0)) {
		return 'DOWN';
	}
	return 'SAME';
};

export const diffServices = (previous: FinancialServiceDTO[] = [], current: FinancialServiceDTO[] = []): ServiceDiff[] => {
	const names = Array.from(new Set([...current.map(item => item.service), ...previous.map(item => item.service)]));
	return names.map(service => {
		const before = previous.find(item => item.service === service);
		const after = current.find(item => item.service === service);
		return { service, previous: before, current: after, change: changeOf(before, after) };
	});
};

const itemKey = (item: FinancialNominalItemDTO) => `${item.employeeId ?? item.employeeName}`;

const mark = (items: FinancialNominalItemDTO[], other: FinancialNominalItemDTO[], missing: ItemSide): ItemDiff[] => {
	const available = new Map<string, number>();
	other.forEach(item => available.set(itemKey(item), (available.get(itemKey(item)) || 0) + 1));
	return items.map(item => {
		const count = available.get(itemKey(item)) || 0;
		if (count > 0) {
			available.set(itemKey(item), count - 1);
			return { item, side: 'SAME' as ItemSide };
		}
		return { item, side: missing };
	});
};

export const diffItems = (previous: FinancialNominalItemDTO[] = [], current: FinancialNominalItemDTO[] = []) => ({
	previous: mark(previous, current, 'REMOVED'),
	current: mark(current, previous, 'ADDED'),
});
